<template>
  <!-- TODO: -->
  <div
    class="autocomplete"
    :class="[
      { 'autocomplete--has-value': hasValue },
      { 'autocomplete--disabled': disabled },
      { 'autocomplete--validation': required }
    ]"
  >
    <v-autocomplete
      v-if="multiple"
      v-model="model"
      :placeholder="placeholder"
      prepend-inner-icon="search"
      :search-input.sync="search"
      :items="entries"
      :chips="multiple"
      :multiple="multiple"
      :loading="isLoading"
      :disabled="isLoading || disabled"
      :readonly="readonly"
      :autofocus="autofocus"
      :required="required"
      :rules="rules"
      :error="!isValid"
      color="primary"
      item-text="name"
      item-value="name"
      return-object
      hide-no-data
      hide-selected
      hide-details
      :noDataText="$t(`${namespace}.noDataText`)"
      outlined
      @keyup="handleSearch"
      @input="emitValues"
      @blur="handleBlur"
      @click="requestOnClick && searchApi()"
    >
      <template slot="selection" slot-scope="data">
        <v-chip
          :data-costCenter-id="data.item.id"
          class="chip--select-multi"
          :title="data.item.name"
          close
          small
          @input="remove(data.item)"
        >
          {{ data.item.name | ellipsis(35) }}
        </v-chip>
      </template>
    </v-autocomplete>

    <v-autocomplete
      v-else
      v-model="model"
      :placeholder="placeholder"
      prepend-inner-icon="search"
      :search-input.sync="search"
      :items="entries"
      :loading="isLoading"
      :disabled="isLoading || disabled"
      :readonly="readonly"
      :autofocus="autofocus"
      :required="required"
      :rules="rules"
      :error="!isValid"
      color="primary"
      item-text="name"
      item-value="name"
      :noDataText="$t(`${namespace}.noDataText`)"
      return-object
      hide-no-data
      hide-selected
      hide-details
      clearable
      outlined
      @keyup="handleSearch"
      @input="emitValues"
      @blur="handleBlur"
      @click="requestOnClick && searchApi()"
    />
    <div v-if="hideDetails" class="autocomplete__error error--text">
      <span v-if="required && !isValid">{{ validateMessage || $t(`${namespace}.validationMessage`) }}</span>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { isValidArray, isValidObject, catchError } from 'corp-front-vue-commons/src/utils/functions'
import debounce from 'lodash.debounce'

export default {
  name: 'AutoComplete',
  props: {
    data: {
      type: [Array, Object],
      default: () => []
    },
    kind: {
      type: String
    },
    companyId: {
      type: [String, Number]
    },
    multiple: {
      type: Boolean,
      default: false
    },
    isValid: {
      type: Boolean,
      default: true
    },
    required: {
      type: Boolean,
      default: false
    },
    validateMessage: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    employeeId: {
      type: [String, Number],
      default: null
    },
    hideDetails: {
      type: Boolean,
      default: true
    },
    requestOnClick: {
      type: Boolean,
      default: false
    },
    costCenterId: {
      type: [String, Number]
    },
    autofocus: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState({
      selectedCompany: ({ company }) => company.selectedCompany
    }),

    getCompanyId () {
      const selectedCompany = this.selectedCompany && this.selectedCompany.id
        ? this.selectedCompany.id
        : null
      return this.companyId || selectedCompany
    },

    hasValue () {
      return (Array.isArray(this.model) && this.model.length) ||
      (this.model && typeof this.model === 'object' && Object.keys(this.model).length)
    }
  },
  data () {
    return {
      namespace: 'Components.AutoComplete',
      model: null,
      search: null,
      entries: [],
      isLoading: false,
      rules: [],
      page: 1,
      limitPerPage: 25,
      firstLoad: true,
      preventEmit: false
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    isValidArray (val) {
      return isValidArray(val)
    },

    isValidObject (val) {
      return isValidObject(val)
    },

    setLoadingValue (boolean = true) {
      this.isLoading = boolean
    },

    validateProperties () {
      const kinds = ['costCenter', 'employee', 'account', 'project', 'webCallEmployee', 'webCallCostCenter', 'webCallCompany', 'company', 'owners', 'webCallProject', 'rideCredit', 'companyGroup']
      const checkKind = kinds.find(item => item === this.kind)

      if (!checkKind) {
        console.warn(`AutoComplete component error: The value for prop "kind" is not valid, use ${kinds.join(', ')}`)
        return
      }

      if (this.kind === 'webCallCostCenter' && !this.employeeId) {
        console.warn('AutoComplete component error: employeeId property is not valid.')
        return
      }

      if (this.multiple && this.data && !Array.isArray(this.data)) {
        console.warn('AutoComplete component error: The "data" property must be an array.')
      }
    },

    remove (item) {
      // update model
      let obj = this.model.find(x => x.name === item.name)
      let ind = this.model.indexOf(obj)
      if (ind >= 0) this.model.splice(ind, 1)

      // update - duplicated code to avoid mutating prop directly
      obj = this.data.find(x => x.name === item.name)
      ind = this.data.indexOf(obj)
      if (ind >= 0) this.data.splice(ind, 1)
      this.$emit('input', this.model)
    },

    async getCostCenter () {
      this.setLoadingValue(true)

      try {
        const { data: res } = await this.$apiService.costCenter.get(
          { company: this.getCompanyId },
          { page: this.page },
          { limit: this.limitPerPage },
          { search: this.search }
        )

        this.entries = [...res.data]

        if (!this.entries.length) {
          this.showAlert({
            type: 'info',
            message: this.$t(`${this.namespace}.costCenterEmpty`)
          })
        }
      } catch (error) {
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.costCenterError`)
        })
        catchError(error)
      }

      this.setLoadingValue(false)
    },

    async getEmployee () {
      this.setLoadingValue(true)

      try {
        const { data: res } = await this.$apiService.employee.get(
          { company: this.getCompanyId },
          { page: this.page },
          { limit: this.limitPerPage },
          { search: this.search }
        )

        this.entries = [...res.data]

        if (!this.entries.length) {
          this.showAlert({
            type: 'info',
            message: this.$t(`${this.namespace}.employeeEmpty`)
          })
        }
      } catch (error) {
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.employeeError`)
        })
        catchError(error)
      }

      this.setLoadingValue(false)
    },

    async getAccounts () {
      this.setLoadingValue(true)

      try {
        const { data: res } = await this.$apiService.accounts.get(
          this.getCompanyId,
          { page: this.page },
          { limit: this.limitPerPage },
          { name: this.search }
        )

        this.entries = [...res.data]

        if (!this.entries.length) {
          this.showAlert({
            type: 'info',
            message: this.$t(`${this.namespace}.accountEmpty`)
          })
        }
      } catch (error) {
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.accountError`)
        })
        catchError(error)
      }

      this.setLoadingValue(false)
    },

    async getProject () {
      this.setLoadingValue(true)

      try {
        const { data: res } = await this.$apiService.project.get(
          { company: this.getCompanyId },
          { page: this.page },
          { limit: this.limitPerPage },
          { search: this.search }
        )

        this.entries = [...res.data]

        if (!this.entries.length) {
          this.showAlert({
            type: 'info',
            message: this.$t(`${this.namespace}.projectEmpty`)
          })
        }
      } catch (error) {
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.projectError`)
        })
        catchError(error)
      }

      this.setLoadingValue(false)
    },

    async getWebCallEmployee () {
      this.setLoadingValue(true)

      try {
        const { data: res } = await this.$apiService.webcall.getEmployee(
          { companyID: this.getCompanyId },
          { page: this.page },
          { limit: this.limitPerPage },
          { search: this.search }
        )

        this.entries = [...res.data]

        if (!this.entries.length) {
          this.showAlert({
            type: 'info',
            message: this.$t(`${this.namespace}.webCallEmployeeEmpty`)
          })
        }
      } catch (error) {
        error.response && error.response.status === 404
          ? this.showAlert({ type: 'info', message: this.$t(`${this.namespace}.webCallEmployeeNotFound`) })
          : this.showAlert({ type: 'error', message: this.$t(`${this.namespace}.webCallEmployeeError`) })
        catchError(error)
      }

      this.setLoadingValue(false)
    },

    async getWebCallCostCenter () {
      this.setLoadingValue(true)

      try {
        const { data: res } = await this.$apiService.getWebCallCostCenter(
          this.employeeId,
          { company: this.getCompanyId },
          { page: this.page },
          { limit: this.limitPerPage },
          { search: this.search }
        )

        this.entries = [...res.data]

        if (!this.entries.length) {
          this.showAlert({
            type: 'info',
            message: this.$t(`${this.namespace}.webCallCostCenterEmpty`)
          })
        }
      } catch (error) {
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.webCallCostCenterError`)
        })
        catchError(error)
      }

      this.setLoadingValue(false)
    },

    async getWebCallProject () {
      this.setLoadingValue(true)

      try {
        const { data: res } = await this.$apiService.webcall.getProject(
          this.costCenterId,
          { page: this.page },
          { limit: this.limitPerPage },
          { search: this.search }
        )

        this.entries = [...res.data]

        if (!this.entries.length) {
          this.showAlert({
            type: 'info',
            message: this.$t(`${this.namespace}.projectEmpty`)
          })
        }
      } catch (error) {
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.projectError`)
        })
        catchError(error)
      }

      this.setLoadingValue(false)
    },

    async getWebCallCompanies () {
      this.setLoadingValue(true)

      try {
        const { data: res } = await this.$apiService.webcall.getCompany()

        // TO DO
        this.entries = [...res.data].filter(company => company.status.toLowerCase() === 'enabled')

        if (!this.entries.length) {
          this.showAlert({
            type: 'info',
            message: this.$t(`${this.namespace}.webCallCompanyEmpty`)
          })
        }
      } catch (error) {
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.webCallCompanyError`)
        })
        catchError(error)
      }

      this.setLoadingValue(false)
    },

    async getCompany () {
      if (!this.search) return

      this.setLoadingValue(true)

      try {
        let search = this.search
        if (search.length === 18 && /\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}/.test(search)) {
          search = search.replace(/[-./]/g, '')
        }

        const { data: res } = await this.$apiService.company.get(
          { page: 1 },
          { limit: 50 },
          { search }
        )

        this.entries = [...res.data]

        if (!this.entries.length) {
          this.showAlert({
            type: 'info',
            message: this.$t(`${this.namespace}.webCallCompanyEmpty`)
          })
        }
      } catch (error) {
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.webCallCompanyError`)
        })
        catchError(error)
      }

      this.setLoadingValue(false)
    },

    async getOwners () {
      if (!this.search) return

      this.setLoadingValue(true)

      try {
        const { data: res } = await this.$apiService.accounts.get(
          this.getCompanyId,
          { page: this.page },
          { limit: this.limitPerPage },
          { profile: 'OwnerUser' },
          { name: this.search }
        )

        this.entries = [...res.data]

        if (!this.entries.length) {
          this.showAlert({
            type: 'info',
            message: this.$t(`${this.namespace}.accountEmpty`)
          })
        }
      } catch (error) {
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.accountError`)
        })
        catchError(error)
      }

      this.setLoadingValue(false)
    },

    async getApiTaxiJobById () {
      if (!this.search) return

      this.setLoadingValue(true)

      try {
        const { data: res } = await this.$apiService.wallet.getApiTaxiJobById(this.search)

        if (res && !res.valid) {
          const error = this.isValidArray(res.reasons) ? res.reasons[0] : res.message
          this.showAlert({
            type: 'error',
            message: error || this.$t(`${this.namespace}.rideCreditError`)
          })
        } else {
          const arrRides = res.content ? [{ ...res.content }] : []

          this.entries = arrRides
            .filter(item => item.orderId)
            .map(item => ({
              id: item.id,
              name: item.detail.apiInfo.apiTaxiJobId,
              apiTaxiJobId: item.detail.apiInfo.apiTaxiJobId,
              ride: item
            }))

          if (!this.entries.length) {
            this.showAlert({
              type: 'info',
              message: this.$t(`${this.namespace}.rideCreditEmpty`)
            })
          }
        }
      } catch (error) {
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.rideCreditError`)
        })
        catchError(error)
      }

      this.setLoadingValue(false)
    },

    async getCompanyGroup () {
      if (!this.search) return

      this.setLoadingValue(true)

      try {
        const name = this.search
        const { data: res } = await this.$apiService.companyGroup.get(
          { page: 1 },
          { limit: 25 },
          { name }
        )

        if (!this.isValidArray(res.data)) {
          this.showAlert({
            type: 'info',
            message: this.$t(`${this.namespace}.companyGroupEmpty`)
          })
        } else {
          this.entries = res.data.map(group => group.companyGroup)
        }
      } catch (err) {
        let msg = this.$t(`${this.namespace}.companyGroupError`)

        if (err && err.response && err.response.data && this.isValidObject(err.response.data.errors)) {
          const errors = err.response.data.errors
          const key = Object.keys(errors)
          if (errors[key]) msg = errors[key][0]
        }

        this.showAlert({
          type: 'error',
          message: msg
        })
      }

      this.setLoadingValue(false)
    },

    searchApi () {
      if (this.kind === 'costCenter') {
        this.getCostCenter()
        return
      }

      if (this.kind === 'employee') {
        this.getEmployee()
        return
      }

      if (this.kind === 'account') {
        this.getAccounts()
        return
      }

      if (this.kind === 'project') {
        this.getProject()
        return
      }

      if (this.kind === 'webCallEmployee') {
        this.getWebCallEmployee()
        return
      }

      if (this.kind === 'webCallCostCenter') {
        this.getWebCallCostCenter()
        return
      }

      if (this.kind === 'webCallProject') {
        this.getWebCallProject()
        return
      }

      if (this.kind === 'webCallCompany') {
        this.getWebCallCompanies()
        return
      }

      if (this.kind === 'company') {
        this.getCompany()
        return
      }

      if (this.kind === 'owners') {
        this.getOwners()
        return
      }

      if (this.kind === 'rideCredit') {
        this.getApiTaxiJobById()
      }

      if (this.kind === 'companyGroup') {
        this.getCompanyGroup()
      }
    },

    handleSearch: debounce(function (val) {
      this.searchApi()
    }, 1000),

    setValue () {
      if (Array.isArray(this.data)) {
        const model = Array.isArray(this.model) ? this.model : []
        this.model = [...this.data].concat(model)
        this.entries = [...this.data].concat(model)
        return
      }

      if (this.data) {
        this.model = this.data
        this.entries = [{ ...this.data }]
      }
    },

    emitValues (val) {
      if (this.multiple) {
        this.search = null
        this.setValue()
      }

      this.model = val
      this.$emit('input', this.model)
    },

    handleBlur () {
      this.$emit('blur')

      if (!this.preventEmit) {
        this.$emit('input', this.model)
        this.preventEmit = true
        setTimeout(() => { this.preventEmit = false }, 1000)
      }
    }
  },
  watch: {
    search (val) {
      if (!this.multiple && !val) {
        this.$emit('reset', null)
      }
    },

    model (val) {
      if (this.firstLoad) {
        this.firstLoad = false
      }
    }
  },
  mounted () {
    this.validateProperties()
    this.setValue()
  }
}
</script>

<style lang="scss">
.autocomplete__error {
  bottom: 0;
  display: block;
  font-size: 10px;
  left: 0;
  position: absolute;
  right: 0;
  z-index: 1;
}

.autocomplete {
  min-height: 52px;
  position: relative;

  &.autocomplete--validation {
    min-height: 74px;
    margin-bottom: 12px;
  }

  &.autocomplete--has-value {
  }

  &.autocomplete--disabled {
  }
}
</style>
